import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ValidationError } from '../models/error.model';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<ToastMessage[]>([]);
  public toasts$ = this.toastsSubject.asObservable();

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private addToast(toast: ToastMessage): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, toast]);

    // Auto remove after duration
    if (toast.duration !== 0) {
      setTimeout(() => {
        this.removeToast(toast.id);
      }, toast.duration || 5000);
    }
  }

  success(title: string, message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      type: 'success',
      title,
      message,
      duration
    });
  }

  error(title: string, message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      type: 'error',
      title,
      message,
      duration
    });
  }

  info(title: string, message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      type: 'info',
      title,
      message,
      duration
    });
  }

  warning(title: string, message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      type: 'warning',
      title,
      message,
      duration
    });
  }

  removeToast(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(toast => toast.id !== id));
  }

  clearAll(): void {
    this.toastsSubject.next([]);
  }

  /**
   * Display validation errors in a toast notification
   * @param title - The title of the toast
   * @param errors - Array of validation errors to display
   * @param duration - Optional duration for the toast
   */
  validationError(title: string, errors: ValidationError[], duration?: number): void {
    const errorMessages = errors.map(error => `${error.field}: ${error.message}`).join('\n');
    this.addToast({
      id: this.generateId(),
      type: 'error',
      title,
      message: errorMessages,
      duration: duration || 8000 // Longer duration for validation errors
    });
  }
}
